/**
 * System Parameters
 *
 * Read-only dashboard for feature flags, logging configuration, and environment settings.
 * Feature flags are grouped by severity: Safety Critical, Integrations, Diagnostics, Mobile.
 * Environment configuration is organized into collapsible, searchable sections.
 *
 * Access: Owner and Admin roles only
 * Route: /admin/metrics
 * Backend: GET /admin/feature-flags
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  TextField,
  InputAdornment,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FlagIcon from '@mui/icons-material/Flag';
import CloudIcon from '@mui/icons-material/Cloud';
import PaymentIcon from '@mui/icons-material/Payment';
import EmailIcon from '@mui/icons-material/Email';
import ExtensionIcon from '@mui/icons-material/Extension';
import KeyIcon from '@mui/icons-material/Key';
import SettingsIcon from '@mui/icons-material/Settings';
import StorageIcon from '@mui/icons-material/Storage';
import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ShieldIcon from '@mui/icons-material/Shield';
import BugReportIcon from '@mui/icons-material/BugReport';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Title } from 'react-admin';
import authInstance from '../services/httpClient';
import { usePermissions } from '../context/PermissionContext';
import { useAdminTheme } from '../admin-theme';
import Logger from '../utils/Logger';

// ============================================================================
// Types
// ============================================================================

interface FeatureFlag {
  name: string;
  value: boolean;
  category: 'danger' | 'warning' | 'info';
  description: string;
  productionWarning?: string;
  source?: 'harness' | 'env';
}

interface FeatureFlagIssue {
  flag: string;
  message: string;
  severity: 'critical' | 'warning';
}

interface LoggingConfig {
  level: string;
  requestLogging: boolean;
  requestLogLevel: string;
  prettyPrint: boolean;
  dbLogging: boolean;
}

interface ConfigItem {
  key: string;
  value: string;
  sensitive: boolean;
  configured: boolean;
  description: string;
}

interface ConfigSection {
  id: string;
  title: string;
  icon: string;
  items: ConfigItem[];
}

interface ConfigSummary {
  totalSections: number;
  totalItems: number;
  configured: number;
  notConfigured: number;
  sensitiveCount: number;
}

interface FeatureFlagsResponse {
  environment: string;
  isProduction: boolean;
  timestamp: string;
  flags: FeatureFlag[];
  issues: FeatureFlagIssue[];
  logging: LoggingConfig;
  summary: {
    total: number;
    enabled: number;
    disabled: number;
    dangerEnabled: number;
    warningEnabled: number;
  };
  configSections: ConfigSection[];
  configSummary: ConfigSummary;
}

// ============================================================================
// Flag Group Definitions
// ============================================================================

interface FlagGroup {
  id: string;
  title: string;
  icon: React.ReactNode;
  color: string;
  glow: string;
  flagNames: string[];
  description: string;
}

// ============================================================================
// Section Icon Map
// ============================================================================

const SECTION_ICONS: Record<string, React.ReactNode> = {
  flag: <FlagIcon fontSize="small" />,
  cloud: <CloudIcon fontSize="small" />,
  payment: <PaymentIcon fontSize="small" />,
  email: <EmailIcon fontSize="small" />,
  extension: <ExtensionIcon fontSize="small" />,
  key: <KeyIcon fontSize="small" />,
  settings: <SettingsIcon fontSize="small" />,
  storage: <StorageIcon fontSize="small" />,
  monitoring: <MonitorHeartIcon fontSize="small" />,
};

// ============================================================================
// Pulse animation for status dots
// ============================================================================

const pulseKeyframes = {
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1, transform: 'scale(1)' },
    '50%': { opacity: 0.6, transform: 'scale(1.3)' },
  },
};

// ============================================================================
// Component
// ============================================================================

const MetricsDashboard: React.FC = () => {
  const { role, loading: permissionsLoading } = usePermissions();
  const { mode, colors } = useAdminTheme();
  const [data, setData] = useState<FeatureFlagsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [configSearch, setConfigSearch] = useState('');
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Flag groups — defines the 4 category cards
  const flagGroups: FlagGroup[] = useMemo(() => [
    {
      id: 'safety',
      title: 'Safety Critical',
      icon: <ShieldIcon />,
      color: colors.error,
      glow: `${colors.error}40`,
      description: 'Test modes that must NEVER be enabled in production',
      flagNames: [
        'PAYGATE_TEST_MODE',
        'OZOW_IS_TEST',
        'DEAR_MOCK_SALES',
        'E2E_TEST_MODE_ENABLED',
        'UBER_ROBO_COURIER_ENABLED',
      ],
    },
    {
      id: 'integrations',
      title: 'Integrations',
      icon: <ExtensionIcon />,
      color: colors.info,
      glow: `${colors.info}40`,
      description: 'Backend service integrations and communication',
      flagNames: [
        'DEAR_SINGLE_SALE_ENDPOINT',
        'DEAR_STOCK_WEBHOOK_ENABLED',
        'OZOW_USE_ONEAPI',
        'SOCKET_IO_ENABLED',
        'PAYMENT_CHECKSUM_VALIDATION',
        'BACKEND_SENTRY_ENABLED',
        'CRON_ENABLED',
        'QUEUE_ENABLED',
      ],
    },
    {
      id: 'diagnostics',
      title: 'Diagnostics',
      icon: <BugReportIcon />,
      color: colors.warning,
      glow: `${colors.warning}40`,
      description: 'Debug and logging flags — may impact performance',
      flagNames: [
        'LOG_LEVEL',
        'IS_DEVELOPER_MESSAGE_ENABLED',
        'ENABLE_REQUEST_LOGGING',
        'ENABLE_PRETTY_LOG',
        'ENABLE_DB_LOGGING',
      ],
    },
    {
      id: 'mobile',
      title: 'Mobile App',
      icon: <PhoneIphoneIcon />,
      color: colors.purple,
      glow: `${colors.purple}40`,
      description: 'Mobile-specific feature toggles',
      flagNames: [
        'MOBILE_SENTRY_ENABLED',
        'ENABLE_LOGROCKET',
        'BIOMETRIC_AUTH_ENABLED',
        'ENABLE_AIRBRIDGE',
      ],
    },
  ], [colors]);

  const fetchData = useCallback(async () => {
    try {
      const response = await authInstance.get('admin/feature-flags');
      setData(response.data);
      setError(null);
      setLastUpdate(new Date());
    } catch (err: any) {
      Logger.error('Failed to fetch system parameters:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, []);

  const copyToClipboard = useCallback((text: string, key: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  }, []);

  // Build a map of flag name → flag data for O(1) lookups
  const flagMap = useMemo(() => {
    if (!data?.flags) return new Map<string, FeatureFlag>();
    const map = new Map<string, FeatureFlag>();
    for (const flag of data.flags) {
      map.set(flag.name, flag);
    }
    return map;
  }, [data?.flags]);

  // Filter config items
  const filterItems = useCallback((items: ConfigItem[]): ConfigItem[] => {
    if (!configSearch.trim()) return items;
    const search = configSearch.toLowerCase();
    return items.filter(
      item =>
        item.key.toLowerCase().includes(search) ||
        item.value.toLowerCase().includes(search) ||
        item.description.toLowerCase().includes(search)
    );
  }, [configSearch]);

  // ============================================================================
  // Access control
  // ============================================================================

  if (permissionsLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
        <CircularProgress sx={{ color: colors.info }} />
      </Box>
    );
  }

  if (!role || !['owner', 'admin'].includes(role)) {
    return (
      <Box sx={{ p: 3 }}>
        <Title title="System Parameters" />
        <Alert severity="error">Access Denied: Owner or Admin role required.</Alert>
      </Box>
    );
  }

  // ============================================================================
  // Render
  // ============================================================================

  const hasIssues = data?.issues && data.issues.length > 0;
  const dangerCount = data?.summary?.dangerEnabled ?? 0;

  return (
    <Box sx={{ p: 3, bgcolor: colors.bgDeepest, minHeight: '100vh', ...pulseKeyframes }}>
      <Title title="System Parameters" />

      {/* ================================================================ */}
      {/* Glass Header                                                     */}
      {/* ================================================================ */}
      <Box
        sx={{
          background: colors.bgGlass,
          backdropFilter: 'blur(12px)',
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          p: 2.5,
          mb: 3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: colors.textPrimary,
              letterSpacing: '-0.02em',
            }}
          >
            System Parameters
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 0.5 }}>
            Feature flags, environment configuration, and system health
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {data && (
            <Chip
              label={data.environment.toUpperCase()}
              size="small"
              sx={{
                bgcolor: data.isProduction ? `${colors.error}20` : `${colors.warning}20`,
                color: data.isProduction ? colors.error : colors.warning,
                border: `1px solid ${data.isProduction ? colors.error : colors.warning}60`,
                fontWeight: 700,
                fontSize: '0.7rem',
                letterSpacing: '0.05em',
              }}
            />
          )}
          {lastUpdate && (
            <Typography variant="caption" sx={{ color: colors.textMuted }}>
              {lastUpdate.toLocaleTimeString()}
            </Typography>
          )}
          <Tooltip title="Refresh">
            <IconButton
              onClick={fetchData}
              disabled={loading}
              sx={{ color: colors.info, '&:hover': { bgcolor: `${colors.info}15` } }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ================================================================ */}
      {/* Critical Issues Banner                                           */}
      {/* ================================================================ */}
      {hasIssues && (
        <Box
          sx={{
            bgcolor: `${colors.error}15`,
            border: `1px solid ${colors.error}60`,
            borderRadius: 2,
            p: 2,
            mb: 3,
            display: 'flex',
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          <WarningAmberIcon sx={{ color: colors.error, mt: 0.3 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ color: colors.error, fontWeight: 700, mb: 0.5 }}>
              {dangerCount} Critical Issue{dangerCount !== 1 ? 's' : ''} Detected
            </Typography>
            {data!.issues.map((issue, idx) => (
              <Typography key={idx} variant="body2" sx={{ color: colors.textSecondary }}>
                <strong style={{ color: colors.error }}>{issue.flag}</strong>: {issue.message}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {/* ================================================================ */}
      {/* Summary Stats Bar                                                */}
      {/* ================================================================ */}
      {data?.summary && (
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 3,
            flexWrap: 'wrap',
          }}
        >
          {[
            { label: 'Total Flags', value: data.summary.total, color: colors.textPrimary },
            { label: 'Enabled', value: data.summary.enabled, color: colors.success },
            { label: 'Disabled', value: data.summary.disabled, color: colors.textMuted },
            ...(data.summary.dangerEnabled > 0
              ? [{ label: 'Danger Active', value: data.summary.dangerEnabled, color: colors.error }]
              : []),
          ].map((stat) => (
            <Box
              key={stat.label}
              sx={{
                bgcolor: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: 1.5,
                px: 2,
                py: 1,
                minWidth: 100,
              }}
            >
              <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem' }}>
                {stat.label}
              </Typography>
              <Typography variant="h5" sx={{ color: stat.color, fontWeight: 700 }}>
                {stat.value}
              </Typography>
            </Box>
          ))}

          {/* Logging chips */}
          {data.logging && (
            <Box
              sx={{
                bgcolor: colors.bgCard,
                border: `1px solid ${colors.border}`,
                borderRadius: 1.5,
                px: 2,
                py: 1,
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="caption" sx={{ color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.65rem', mr: 0.5 }}>
                Logging
              </Typography>
              <Chip
                label={data.logging.level.toUpperCase()}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  bgcolor: ['debug', 'trace'].includes(data.logging.level)
                    ? `${colors.warning}20`
                    : `${colors.success}20`,
                  color: ['debug', 'trace'].includes(data.logging.level)
                    ? colors.warning
                    : colors.success,
                }}
              />
              {data.logging.prettyPrint && (
                <Chip label="PRETTY" size="small" sx={{ height: 22, fontSize: '0.65rem', bgcolor: `${colors.warning}20`, color: colors.warning }} />
              )}
              {data.logging.dbLogging && (
                <Chip label="SQL" size="small" sx={{ height: 22, fontSize: '0.65rem', bgcolor: `${colors.warning}20`, color: colors.warning }} />
              )}
              {data.logging.requestLogging && (
                <Chip label="HTTP" size="small" sx={{ height: 22, fontSize: '0.65rem', bgcolor: `${colors.info}20`, color: colors.info }} />
              )}
            </Box>
          )}
        </Box>
      )}

      {/* ================================================================ */}
      {/* Feature Flag Groups                                              */}
      {/* ================================================================ */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress sx={{ color: colors.info }} />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2.5, mb: 4 }}>
          {flagGroups.map((group) => {
            const groupFlags = group.flagNames
              .map(name => flagMap.get(name))
              .filter((f): f is FeatureFlag => f != null);

            const enabledCount = groupFlags.filter(f => f.value).length;
            const hasActiveFlags = enabledCount > 0;
            const isSafety = group.id === 'safety';
            const hasDangerActive = isSafety && enabledCount > 0;

            return (
              <Box
                key={group.id}
                sx={{
                  bgcolor: colors.bgCard,
                  border: `1px solid ${hasDangerActive ? colors.error : colors.border}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: hasDangerActive
                    ? `0 0 20px ${colors.error}30`
                    : hasActiveFlags
                    ? `0 0 15px ${group.glow}`
                    : 'none',
                  '&:hover': {
                    borderColor: group.color,
                    boxShadow: `0 0 20px ${group.glow}`,
                  },
                }}
              >
                {/* Group Header */}
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: `1px solid ${colors.border}`,
                    background: `linear-gradient(135deg, ${group.color}08, transparent)`,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: `${group.color}15`,
                        color: group.color,
                        border: `1px solid ${group.color}30`,
                      }}
                    >
                      {group.icon}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.textPrimary, lineHeight: 1.2 }}>
                        {group.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.7rem' }}>
                        {group.description}
                      </Typography>
                    </Box>
                  </Box>

                  <Chip
                    label={`${enabledCount}/${groupFlags.length}`}
                    size="small"
                    sx={{
                      height: 24,
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      bgcolor: hasDangerActive
                        ? `${colors.error}20`
                        : enabledCount > 0
                        ? `${group.color}15`
                        : `${colors.textMuted}15`,
                      color: hasDangerActive
                        ? colors.error
                        : enabledCount > 0
                        ? group.color
                        : colors.textMuted,
                      border: `1px solid ${hasDangerActive ? colors.error : enabledCount > 0 ? group.color : colors.textMuted}30`,
                    }}
                  />
                </Box>

                {/* Flag List */}
                <Box sx={{ p: 1 }}>
                  {groupFlags.map((flag) => (
                    <Box
                      key={flag.name}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        transition: 'background 0.15s',
                        '&:hover': { bgcolor: colors.bgElevated },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 0 }}>
                        {/* Status dot */}
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            flexShrink: 0,
                            bgcolor: flag.value
                              ? flag.category === 'danger'
                                ? colors.error
                                : colors.success
                              : colors.textMuted,
                            boxShadow: flag.value
                              ? `0 0 6px ${flag.category === 'danger' ? colors.error : colors.success}`
                              : 'none',
                            animation: flag.value && flag.category === 'danger'
                              ? 'pulse 2s infinite'
                              : 'none',
                          }}
                        />

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.78rem',
                                color: colors.textPrimary,
                                fontWeight: 500,
                              }}
                            >
                              {flag.name}
                            </Typography>
                            {flag.source === 'harness' && (
                              <Tooltip title="Managed by Harness Feature Flags">
                                <Chip
                                  label="FF"
                                  size="small"
                                  sx={{
                                    height: 16,
                                    fontSize: '0.55rem',
                                    fontWeight: 700,
                                    bgcolor: `${colors.info}15`,
                                    color: colors.info,
                                    border: `1px solid ${colors.info}30`,
                                    '& .MuiChip-label': { px: 0.5 },
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{ color: colors.textMuted, fontSize: '0.7rem', lineHeight: 1.3 }}
                          >
                            {flag.description}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Status badge */}
                      <Chip
                        icon={flag.value ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <CancelIcon sx={{ fontSize: 14 }} />}
                        label={flag.value ? 'ON' : 'OFF'}
                        size="small"
                        sx={{
                          height: 24,
                          fontWeight: 700,
                          fontSize: '0.7rem',
                          ml: 1,
                          flexShrink: 0,
                          bgcolor: flag.value
                            ? flag.category === 'danger'
                              ? `${colors.error}20`
                              : `${colors.success}20`
                            : `${colors.textMuted}10`,
                          color: flag.value
                            ? flag.category === 'danger'
                              ? colors.error
                              : colors.success
                            : colors.textMuted,
                          border: `1px solid ${
                            flag.value
                              ? flag.category === 'danger'
                                ? `${colors.error}40`
                                : `${colors.success}40`
                              : `${colors.textMuted}20`
                          }`,
                          '& .MuiChip-icon': {
                            color: 'inherit',
                          },
                        }}
                      />
                    </Box>
                  ))}

                  {groupFlags.length === 0 && (
                    <Typography variant="caption" sx={{ color: colors.textMuted, px: 1.5, py: 2, display: 'block' }}>
                      No flags in this group returned by the API yet.
                    </Typography>
                  )}
                </Box>

                {/* Production warning for safety group */}
                {isSafety && (
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderTop: `1px solid ${colors.border}`,
                      bgcolor: hasDangerActive ? `${colors.error}08` : 'transparent',
                    }}
                  >
                    <Typography variant="caption" sx={{ color: hasDangerActive ? colors.error : colors.textMuted, fontSize: '0.7rem' }}>
                      {hasDangerActive
                        ? 'WARNING: Safety-critical flags are active. Verify this is a non-production environment.'
                        : 'All safety-critical flags are off. Production-safe.'}
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {/* Harness info */}
      <Box
        sx={{
          bgcolor: `${colors.info}08`,
          border: `1px solid ${colors.info}20`,
          borderRadius: 1.5,
          px: 2,
          py: 1.5,
          mb: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <FlagIcon sx={{ color: colors.info, fontSize: 18 }} />
        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
          Flags marked <Chip label="FF" size="small" sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, bgcolor: `${colors.info}15`, color: colors.info, mx: 0.5, '& .MuiChip-label': { px: 0.5 } }} /> are managed by Harness Feature Flags with real-time evaluation. Toggle them in the Harness dashboard. Other flags are read from environment variables.
        </Typography>
      </Box>

      {/* ================================================================ */}
      {/* Environment Configuration                                        */}
      {/* ================================================================ */}
      {data?.configSections && data.configSections.length > 0 && (
        <Box>
          {/* Section Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <SettingsIcon sx={{ color: colors.info }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.textPrimary }}>
                Environment Configuration
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {data.configSummary && (
                <>
                  <Chip
                    label={`${data.configSummary.configured} configured`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      bgcolor: `${colors.success}15`,
                      color: colors.success,
                      border: `1px solid ${colors.success}30`,
                    }}
                  />
                  {data.configSummary.notConfigured > 0 && (
                    <Chip
                      label={`${data.configSummary.notConfigured} missing`}
                      size="small"
                      sx={{
                        height: 22,
                        fontSize: '0.7rem',
                        bgcolor: `${colors.warning}15`,
                        color: colors.warning,
                        border: `1px solid ${colors.warning}30`,
                      }}
                    />
                  )}
                  <Chip
                    icon={<LockIcon sx={{ fontSize: 12 }} />}
                    label={`${data.configSummary.sensitiveCount} secrets`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: '0.7rem',
                      bgcolor: `${colors.textMuted}10`,
                      color: colors.textMuted,
                      '& .MuiChip-icon': { color: 'inherit' },
                    }}
                  />
                </>
              )}
            </Box>
          </Box>

          {/* Search */}
          <TextField
            size="small"
            placeholder="Search configuration keys, values, descriptions..."
            value={configSearch}
            onChange={(e) => setConfigSearch(e.target.value)}
            sx={{
              mb: 2,
              width: { xs: '100%', sm: 360 },
              '& .MuiOutlinedInput-root': {
                bgcolor: colors.bgCard,
                color: colors.textPrimary,
                '& fieldset': { borderColor: colors.border },
                '&:hover fieldset': { borderColor: colors.info },
                '&.Mui-focused fieldset': { borderColor: colors.info },
              },
              '& .MuiInputAdornment-root': { color: colors.textMuted },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {/* Collapsible Sections */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {data.configSections.map((section) => {
              const filteredItems = filterItems(section.items);
              const isExpanded = expandedSections[section.id] ?? false;

              if (configSearch && filteredItems.length === 0) return null;

              const configuredCount = section.items.filter(i => i.configured).length;
              const totalCount = section.items.length;

              return (
                <Box
                  key={section.id}
                  sx={{
                    bgcolor: colors.bgCard,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 1.5,
                    overflow: 'hidden',
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: colors.borderGlow },
                  }}
                >
                  {/* Section Toggle */}
                  <Box
                    onClick={() => toggleSection(section.id)}
                    sx={{
                      px: 2,
                      py: 1.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                      '&:hover': { bgcolor: colors.bgElevated },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ color: colors.info, display: 'flex' }}>
                        {SECTION_ICONS[section.icon] || <SettingsIcon fontSize="small" />}
                      </Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.textPrimary }}>
                        {section.title}
                      </Typography>
                      <Chip
                        label={`${configuredCount}/${totalCount}`}
                        size="small"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          bgcolor: configuredCount === totalCount
                            ? `${colors.success}15`
                            : `${colors.warning}15`,
                          color: configuredCount === totalCount
                            ? colors.success
                            : colors.warning,
                        }}
                      />
                      {section.items.some(i => i.sensitive) && (
                        <LockIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                      )}
                    </Box>
                    <Box sx={{ color: colors.textMuted, display: 'flex' }}>
                      {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                    </Box>
                  </Box>

                  {/* Section Content */}
                  <Collapse in={isExpanded || !!configSearch}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              width="35%"
                              sx={{
                                color: colors.textMuted,
                                borderColor: colors.border,
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontWeight: 600,
                              }}
                            >
                              Key
                            </TableCell>
                            <TableCell
                              width="40%"
                              sx={{
                                color: colors.textMuted,
                                borderColor: colors.border,
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontWeight: 600,
                              }}
                            >
                              Value
                            </TableCell>
                            <TableCell
                              width="25%"
                              sx={{
                                color: colors.textMuted,
                                borderColor: colors.border,
                                fontSize: '0.7rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                                fontWeight: 600,
                              }}
                            >
                              Description
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {filteredItems.map((item) => (
                            <TableRow
                              key={item.key}
                              sx={{
                                bgcolor: !item.configured ? `${colors.warning}06` : 'inherit',
                                '&:hover': { bgcolor: colors.bgElevated },
                                '& td': { borderColor: colors.border },
                              }}
                            >
                              <TableCell>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontFamily: 'monospace',
                                    fontSize: '0.78rem',
                                    color: colors.textPrimary,
                                  }}
                                >
                                  {item.key}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                {item.sensitive ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <LockIcon sx={{ fontSize: 14, color: colors.textMuted }} />
                                    <Chip
                                      label={item.configured ? 'Configured' : 'Not Set'}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        fontSize: '0.65rem',
                                        bgcolor: item.configured
                                          ? `${colors.success}15`
                                          : `${colors.warning}15`,
                                        color: item.configured
                                          ? colors.success
                                          : colors.warning,
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.78rem',
                                        color: !item.configured ? colors.warning : colors.textPrimary,
                                        fontStyle: !item.configured ? 'italic' : 'normal',
                                      }}
                                    >
                                      {item.value}
                                    </Typography>
                                    {item.configured && item.value !== 'not_set' && (
                                      <Tooltip title={copiedKey === item.key ? 'Copied!' : 'Copy'}>
                                        <IconButton
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            copyToClipboard(item.value, item.key);
                                          }}
                                          sx={{
                                            color: copiedKey === item.key ? colors.success : colors.textMuted,
                                            '&:hover': { color: colors.info },
                                          }}
                                        >
                                          <ContentCopyIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" sx={{ color: colors.textMuted }}>
                                  {item.description}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Collapse>
                </Box>
              );
            })}
          </Box>

          {/* Footer info */}
          <Box
            sx={{
              mt: 2,
              px: 2,
              py: 1.5,
              bgcolor: `${colors.textMuted}08`,
              border: `1px solid ${colors.border}`,
              borderRadius: 1.5,
            }}
          >
            <Typography variant="caption" sx={{ color: colors.textMuted }}>
              Sensitive values (API keys, passwords, secrets) are masked. Configuration is read-only — update via environment variables or Secrets Manager.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MetricsDashboard;
